import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Edit, Trash2, Search, X, History, CalendarIcon, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { IconButtonWithTooltip } from "@/components/ui/icon-button-tooltip";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { useUsers } from "@/hooks/users/useUsers";
import LoaderComponent from "@/components/common/LoaderComponent";
import { formatDate } from "@/utils/dateUtils";
import { useDeleteUser } from "@/hooks/users/useDeleteUser";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useState } from "react";
import { OperationsHistoryModal } from "../common/OperationsHistoryModal";
import { useUserAuditLog } from "@/hooks/users/useUserAuditLog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

export const UsersTable = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedUserForHistory, setSelectedUserForHistory] = useState<any>(null);
  const { deleteUser, deleting } = useDeleteUser();
  
  const { userAuditLogs, auditLogsLoading } = useUserAuditLog(
    selectedUserForHistory?.id || null
  );
  
  const {
    users,
    loading,
    error,
    currentPage,
    totalPages,
    totalCount,
    entriesPerPage,
    filterType,
    searchValue,
    setCurrentPage,
    setEntriesPerPage,
    setFilterType,
    setSearchValue,
    handleSearch,
    clearSearch,
    refetch,
  } = useUsers();

  const handleEdit = (id: number) => {
    const qs = searchParams.toString();
    navigate(`/admin/users/edit/${id}${qs ? `?${qs}` : ''}`);
  };

  const handleDeleteClick = (id: number) => {
    setSelectedUserId(id);
    setDeleteDialogOpen(true);
  };

  const handleHistory = (user: any) => {
    setSelectedUserForHistory(user);
    setIsHistoryModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedUserId) {
      const success = await deleteUser(selectedUserId);
      if (success) {
        setDeleteDialogOpen(false);
        setSelectedUserId(null);
        refetch();
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Calculate pagination range
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 10;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 5) {
        for (let i = 1; i <= 7; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 4) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 6; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 2; i <= currentPage + 2; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const startEntry = totalCount > 0 ? (currentPage - 1) * entriesPerPage + 1 : 0;
  const endEntry = Math.min(currentPage * entriesPerPage, totalCount);

  // Client-side pagination: slice the users array
  const paginatedUsers = users.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  if (error) {
    return (
      <div className="space-y-6">
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">Error loading users: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-48 bg-background">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent className="bg-background z-50">
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="user_name">User Name</SelectItem>
              <SelectItem value="user_type">User Type</SelectItem>
              <SelectItem value="updated">Update Date</SelectItem>
              <SelectItem value="last_active">Last Active</SelectItem>
            </SelectContent>
          </Select>
          
          {filterType === 'updated' || filterType === 'last_active' ? (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-48 justify-start text-left font-normal",
                    !searchValue && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {searchValue ? format(new Date(searchValue), "dd/MM/yyyy") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={searchValue ? new Date(searchValue) : undefined}
                  onSelect={(date) => {
                    if (date) {
                      setSearchValue(format(date, "yyyy-MM-dd"));
                    }
                  }}
                  initialFocus
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          ) : (
            <Input 
              className="w-48" 
              placeholder="Enter value"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyPress={handleKeyPress}
            />
          )}
          <Button 
            className="bg-purple-600 hover:bg-purple-700 text-white px-8"
            onClick={handleSearch}
            disabled={loading}
          >
            <Search className="h-4 w-4 mr-2" />
            Search
          </Button>
          {searchValue && (
            <Button 
              variant="outline"
              onClick={clearSearch}
              disabled={loading}
            >
              <X className="h-4 w-4 mr-2" />
              Clear
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Show</span>
          <Select 
            value={entriesPerPage.toString()} 
            onValueChange={(value) => setEntriesPerPage(Number(value))}
            disabled={loading}
          >
            <SelectTrigger className="w-20 bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-background z-50">
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
              <SelectItem value="500">500</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">Entries</span>
        </div>
      </div>

      <div id="table-admin-users-container" className="rounded-md border">
        <Table id="table-admin-users">
          <TableHeader>
            <TableRow id="table-admin-users-header-row">
              <TableHead id="header-cell-user-name">User Name</TableHead>
              <TableHead id="header-cell-email">Email Address</TableHead>
              <TableHead id="header-cell-user-type">User Type</TableHead>
              <TableHead id="header-cell-update-date">Update Date</TableHead>
              <TableHead id="header-cell-last-active">Last Active</TableHead>
              <TableHead id="header-cell-actions" className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody id="table-admin-users-body">
            {loading ? (
              <TableRow id="row-users-loading">
                <TableCell colSpan={6} className="text-center py-8">
                  <LoaderComponent size="md" />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow id="row-users-empty">
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              paginatedUsers.map((user) => (
                <TableRow key={user.id} id={`row-user-${user.id}`}>
                  <TableCell id={`cell-user-${user.id}-name`} className="font-medium">{user.user_name || 'N/A'}</TableCell>
                  <TableCell id={`cell-user-${user.id}-email`}>{user.email || 'N/A'}</TableCell>
                  <TableCell id={`cell-user-${user.id}-type`}>
                    <Badge variant="outline" className="bg-purple-50 text-purple-600 border-purple-200">
                      {user.user_type || 'N/A'}
                    </Badge>
                  </TableCell>
                  <TableCell id={`cell-user-${user.id}-updated`}>{user.updated ? new Date(user.updated).toLocaleDateString('en-GB') : 'Not Set'}</TableCell>
                  <TableCell id={`cell-user-${user.id}-last-active`}>{user.last_active ? new Date(user.last_active).toLocaleDateString('en-GB') : 'Not Set'}</TableCell>
                  <TableCell id={`cell-user-${user.id}-actions`} className="text-right">
                    <div className="flex gap-2 justify-end">
                      <IconButtonWithTooltip
                        id={`btn-user-${user.id}-history`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleHistory(user)}
                        tooltip="View History"
                        icon={<History className="h-4 w-4" />}
                      />
                      <IconButtonWithTooltip
                        id={`btn-user-${user.id}-edit`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(user.id)}
                        tooltip="Edit"
                        icon={<Edit className="h-4 w-4" />}
                      />
                      <IconButtonWithTooltip
                        id={`btn-user-${user.id}-delete`}
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDeleteClick(user.id)}
                        tooltip="Delete"
                        icon={<Trash2 className="h-4 w-4" />}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground whitespace-nowrap">
          Showing {startEntry} to {endEntry} of {totalCount} entries
        </div>
        
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious 
                onClick={() => setCurrentPage(currentPage - 1)}
                className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
            
            {getPageNumbers().map((page, index) => (
              <PaginationItem key={index}>
                {page === '...' ? (
                  <span className="px-4 py-2">...</span>
                ) : (
                  <PaginationLink
                    onClick={() => setCurrentPage(page as number)}
                    className={
                      currentPage === page
                        ? "bg-purple-600 text-white hover:bg-purple-700 cursor-pointer"
                        : "hover:bg-purple-100 cursor-pointer"
                    }
                  >
                    {page}
                  </PaginationLink>
                )}
              </PaginationItem>
            ))}
            
            <PaginationItem>
              <PaginationNext 
                onClick={() => setCurrentPage(currentPage + 1)}
                className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the user.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={(e) => {
                // Prevent Radix AlertDialog from auto-closing so the loader is visible
                e.preventDefault();
                void handleDeleteConfirm();
              }}
              disabled={deleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleting ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Deleting...
                </span>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <OperationsHistoryModal
        open={isHistoryModalOpen}
        onOpenChange={setIsHistoryModalOpen}
        entityName={selectedUserForHistory?.user_name || ""}
        entityType="User"
        auditLogs={userAuditLogs}
        loading={auditLogsLoading}
      />
    </div>
  );
};